import { LocalDate, ZonedDateTime, ZoneId } from '@js-joda/core';
import { useEffect, useMemo, useState } from 'react';
import {
  CoachSessionAttendance,
  CoachSessionBasicsFragment,
  CoachSessionFilter,
  CoachSessionOrder,
  CoachSessionStyle,
  CoachSessionType,
  OrderDirection,
  useCoachSessionsLazyQuery,
} from '../../graphql/types';
import { useCoachSessionQueryVariables } from '../../hooks/useQueryParams';
import { stringToLocalDate } from '../../utils/date';
import { notUndefined } from '../../utils/notEmpty';
import {
  SessionFilters,
  sessionAttendanceFilters,
  sessionStyleFilters,
  sessionTypeFilters,
} from './Filters/util';

export type SessionWithGroupingMetaData = {
  isFirstSessionInDay: boolean;
} & CoachSessionBasicsFragment;

export const getSessionsWithGroupingMetaData = (
  sessions: CoachSessionBasicsFragment[],
) => {
  let previousDay: LocalDate | null = null;

  const sessionsWithMetaData: SessionWithGroupingMetaData[] = [];

  sessions.forEach((session) => {
    if (!session.calendar?.start) {
      return;
    }
    let isFirstSessionInDay = false;
    const currentDate = stringToLocalDate(session.calendar.start);
    if (!currentDate.equals(previousDay)) {
      previousDay = currentDate;
      isFirstSessionInDay = true;
    }
    sessionsWithMetaData.push({
      ...session,
      isFirstSessionInDay,
    });
  });

  return sessionsWithMetaData;
};

const orderBy = [
  {
    order: CoachSessionOrder.SessionTime,
    direction: OrderDirection.Asc,
  },
];
const mappedSessionQueryParams: Record<
  SessionFilters,
  CoachSessionAttendance | CoachSessionType | CoachSessionStyle | null
> = {
  [SessionFilters.Attended]: CoachSessionAttendance.Attended,
  [SessionFilters.NoShow]: CoachSessionAttendance.NoShow,
  [SessionFilters.Excused]: CoachSessionAttendance.Excused,
  [SessionFilters.NotRecorded]: null,
  [SessionFilters.Coaching]: CoachSessionType.UserCoachingConsult,
  [SessionFilters.PersonalTraining]: CoachSessionType.PersonalTraining,
  [SessionFilters.Nutrition]: CoachSessionType.UserNutritionConsult,
  [SessionFilters.Virtual]: CoachSessionStyle.Virtual,
  [SessionFilters.InPerson]: CoachSessionStyle.InPerson,
};

export const useCoachSessionsQuery = () => {
  const [loadingMore, setLoadingMore] = useState(false);
  const [{ filters, memberName, dateRange }] = useCoachSessionQueryVariables();

  const {
    attendanceFilterValues,
    sessionTypesFilterValues,
    sessionStyleFilterValues,
  } = useMemo(
    () => ({
      attendanceFilterValues: filters
        .map(
          (filter) =>
            sessionAttendanceFilters.find((f) => f.value === filter)?.value,
        )
        .filter(notUndefined) as SessionFilters[],
      sessionTypesFilterValues: filters
        .map(
          (filter) => sessionTypeFilters.find((f) => f.value === filter)?.value,
        )
        .filter(notUndefined) as SessionFilters[],
      sessionStyleFilterValues: filters
        .map(
          (filter) =>
            sessionStyleFilters.find((f) => f.value === filter)?.value,
        )
        .filter(notUndefined) as SessionFilters[],
    }),
    [filters],
  );

  const groupedSessionFilters = useMemo(
    () =>
      ({
        attendances:
          attendanceFilterValues.length > 0
            ? attendanceFilterValues.map(
                (filter) => mappedSessionQueryParams[filter],
              )
            : undefined,
        sessionTypes:
          sessionTypesFilterValues.length > 0
            ? sessionTypesFilterValues.map(
                (filter) => mappedSessionQueryParams[filter],
              )
            : undefined,
        coachSessionStyle:
          sessionStyleFilterValues.length > 0
            ? sessionStyleFilterValues.map(
                (filter) => mappedSessionQueryParams[filter],
              )
            : undefined,
      }) as CoachSessionFilter,
    [
      attendanceFilterValues,
      sessionStyleFilterValues,
      sessionTypesFilterValues,
    ],
  );

  const inclusiveDateRange = useMemo(
    () => ({
      start: dateRange?.start
        ? ZonedDateTime.parse(dateRange.start)
            .withZoneSameInstant(ZoneId.SYSTEM)
            .withHour(0)
            .withMinute(0)
            .withSecond(0)
            .withFixedOffsetZone()
            .toJSON()
        : undefined,
      end: dateRange?.end
        ? ZonedDateTime.parse(dateRange.end)
            .withZoneSameInstant(ZoneId.SYSTEM)
            .withHour(23)
            .withMinute(59)
            .withSecond(59)
            .withFixedOffsetZone()
            .toJSON()
        : undefined,
    }),
    [dateRange],
  );

  const [, { data, loading, error, fetchMore, refetch: fetch }] =
    useCoachSessionsLazyQuery({
      fetchPolicy: 'cache-first',
      variables: {
        input: {
          first: 15,
        },
        filter: {
          ...groupedSessionFilters,
          memberName,
          dateRange: inclusiveDateRange,
        },
        orderBy,
      },
    });

  const hasNextPage = data?.self?.coachData?.sessions.pageInfo.hasNextPage;
  const fetchMoreSessions = async () => {
    if (!hasNextPage) {
      setLoadingMore(false);
      return;
    }
    setLoadingMore(true);
    await fetchMore({
      variables: {
        input: {
          first: 15,
          after: data?.self?.coachData?.sessions.pageInfo.endCursor,
        },
        filter: {
          ...groupedSessionFilters,
          memberName,
          dateRange: inclusiveDateRange,
        },
        orderBy,
      },
    });
    setLoadingMore(false);
  };

  useEffect(() => {
    fetch({
      input: {
        first: 15,
      },
      filter: {
        ...groupedSessionFilters,
        dateRange: inclusiveDateRange,
        memberName,
      },
      orderBy,
    });
  }, [fetch, memberName, groupedSessionFilters, inclusiveDateRange]);

  const sessionsWithMetaData = getSessionsWithGroupingMetaData(
    data?.self?.coachData?.sessions.edges
      .map(({ node }) => node)
      .sort((a, b) => {
        if (a.calendar?.start && b.calendar?.start) {
          return ZonedDateTime.parse(a.calendar?.start).compareTo(
            ZonedDateTime.parse(b.calendar?.start),
          );
        }
        return 1;
      }) ?? [],
  );
  const filteredSessionCount = data?.self?.coachData?.sessions.count;
  const allSessionsCount = data?.self?.coachData?.allSessions.count;

  return {
    sessions: sessionsWithMetaData,
    allSessionsCount,
    filteredSessionCount,
    error,
    loading,
    loadingMore,
    fetchMoreSessions,
    hasNextPage,
  };
};

export type SessionJoinableState = 'upcoming' | 'joinable' | 'past';

export const isSessionJoinable = (
  session: CoachSessionBasicsFragment,
  now = ZonedDateTime.now(ZoneId.UTC),
) => {
  if (!session.joinableTime || !session.calendar?.end || !session.meetingUrl) {
    return false;
  }

  const sessionJoinableTime = ZonedDateTime.parse(session.joinableTime);
  const sessionEndTime = ZonedDateTime.parse(session.calendar.end);

  if (now.isAfter(sessionJoinableTime) && now.isBefore(sessionEndTime)) {
    return true;
  }

  return false;
};
